import { deepMergeKeepArrays } from "@/features/editor/components/overlay_layers/utils";
import IMDFFeature from "@/libs/imdf/feature";
import type { EditorChanges } from "../types";

/**
 * Merges IMDF features with changes from the editor.
 *
 * This function processes a list of existing IMDF features, applying updates,
 * removing deleted features, and adding new features.
 *
 * @param features - The current list of IMDFFeatures.
 * @param changes - An object containing changes indexed by feature ID.
 * @returns A record of merged IMDF features, with updates applied, deletions removed, and new features added.
 */
export const mergeIMDFFeatures = (
  features: IMDFFeature[] | undefined,
  changes: EditorChanges<IMDFFeature>,
): Record<string, IMDFFeature> => {
  // Merge existing features with updates while skipping deleted ones
  const mergedFeatures = (features ?? []).reduce<Record<string, IMDFFeature>>(
    (acc, feature) => {
      const change = changes[feature.id];
      if (change?.type === "deleted") return acc;

      if (change?.type === "updated") {
        acc[feature.id] = new IMDFFeature(
          deepMergeKeepArrays(feature, change.item) as IMDFFeature,
        );
        return acc;
      }

      acc[feature.id] = feature;
      return acc;
    },
    {},
  );

  // Add new features
  Object.entries(changes).forEach(([id, change]) => {
    if (change.type === "new") {
      mergedFeatures[id] = new IMDFFeature({ ...change.item });
    }
  });

  return mergedFeatures;
};
